import { useMutation, useQuery } from "@vue/apollo-composable";
import { gql } from "@apollo/client/core";
import { useBulkUploadStore } from "@/stores/bulkUploadStore";
import type { LocalStorageUpload, ImportType } from "@/types/bulk-upload-types";

const BULK_UPLOAD_MUTATION = gql`
  mutation bulkUpload($input: bulkUploadInput!) {
    bulkUpload(input: $input) {
      uploadId
      message
      errors
    }
  }
`;

const BULK_UPLOAD_STATUS_QUERY = gql`
  query bulkUploadStatus($uploadId: ID!) {
    bulkUploadStatus(uploadId: $uploadId) {
      id
      importType
      programId
      fileName
      fileUrl
      status
      totalRecords
      processedRecords
      failedRecords
      progressPercentage
      successRate
      errorMessage
      errorCsvUrl
      startedAt
      completedAt
      duration
    }
  }
`;

export function useBulkUpload() {
  const store = useBulkUploadStore();

  // Upload mutation
  const {
    mutate: uploadCsv,
    loading: uploading,
    onDone,
    onError,
  } = useMutation(BULK_UPLOAD_MUTATION);

  // Status query (with polling)
  const fetchStatus = (uploadId: string) => {
    return useQuery(
      BULK_UPLOAD_STATUS_QUERY,
      { uploadId },
      {
        pollInterval: 2000, // Poll every 2 seconds
        fetchPolicy: "network-only",
        enabled: !!uploadId,
      }
    );
  };

  // Upload handler
  const startUpload = async (
    importType: ImportType,
    programId: string,
    file: File
  ): Promise<string | null> => {
    try {
      // Relay-style with top-level "input" parameter
      const result = await uploadCsv({
        input: {
          bulkUploadInput: {
            importType,
            programId,
            csvFile: file,
          },
        },
      });

      const uploadId = result?.data?.bulkUpload?.uploadId;
      const errors = result?.data?.bulkUpload?.errors;
      const message = result?.data?.bulkUpload?.message;

      if (errors && errors.length > 0) {
        throw new Error(errors.join(", "));
      }

      if (uploadId) {
        // Add to store
        const upload: LocalStorageUpload = {
          uploadId,
          importType,
          programId,
          fileName: file.name,
          startedAt: new Date().toISOString(),
        };
        store.addUpload(upload);
        return uploadId;
      }

      return null;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  return {
    uploading,
    startUpload,
    fetchStatus,
    onDone,
    onError,
  };
}
